/**
 * The published legal surface: two anonymous reads, and the two calls that name a person.
 *
 * `GET /api/v1/legal/{kind}` needs no session and must not acquire one: an imprint behind a
 * sign-in is not published, and a privacy policy nobody subject to it can read is not a
 * privacy policy. The generated client sends the session cookie when the browser has one and
 * the endpoint ignores it either way.
 *
 * The two acceptance calls below are the opposite — they name a person, so they need one. They
 * live here rather than in a second module because they read and write the same documents:
 * the hash `acceptLegalDocument` sends is the one `readLegalDocument` served.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type LegalDocument = Schemas['LegalDocumentResponse'];
export type LegalDocumentSummary = Schemas['LegalDocumentSummary'];
export type OutstandingAcceptance = Schemas['OutstandingAcceptance'];
export type Acceptance = Schemas['AcceptanceResponse'];

/** Everything currently published. Kinds counsel has not delivered are simply absent. */
export async function listLegalDocuments(): Promise<LegalDocumentSummary[]> {
  return unwrap(api.GET('/api/v1/legal', {}));
}

/**
 * The same published text as a PDF file — `GET /api/v1/legal/{kind}?format=pdf`.
 *
 * A URL for an anchor rather than a fetch: the server answers `Content-Disposition: attachment`,
 * so the browser downloads it with no blob and no script, and nothing needs a session. The
 * `locale` is the one the page was **served** in, so the file is the text the reader is looking
 * at — the AVV falls back to German, and a Slovak reader who asked for Slovak must not be handed
 * a file in a language the page did not show.
 */
export function legalPdfUrl(kind: string, locale: string): string {
  const search = new URLSearchParams({ locale, format: 'pdf' });
  return `/api/v1/legal/${encodeURIComponent(kind)}?${search.toString()}`;
}

/**
 * One published text. A `404` here is the honest answer for a kind that does not exist yet —
 * the caller renders "not published", never a placeholder.
 */
export async function readLegalDocument(kind: string, locale: string): Promise<LegalDocument> {
  return unwrap(
    api.GET('/api/v1/legal/{kind}', { params: { path: { kind }, query: { locale } } }),
  );
}

/**
 * What this caller still owes acceptance on. **Needs a session**, unlike the two above.
 *
 * A list of facts and never a verdict (`legal/schemas.py:OutstandingResponse`): what it gates
 * is *starting new work*, and the client applies that rule no more than the server does — the
 * notice this feeds is drawn beside the screen, never in front of it.
 */
export async function readOutstandingAcceptances(): Promise<OutstandingAcceptance[]> {
  const response = await unwrap(api.GET('/api/v1/legal-acceptances/outstanding', {}));
  return response.outstanding;
}

/**
 * Record that this caller accepted exactly the text they were shown.
 *
 * The hash is the payload's point, not a checksum on it: the endpoint compares it before it
 * writes and answers `409` on a mismatch, so a tab left open across a republication is told to
 * re-read rather than quietly bound to a version nobody displayed. It therefore comes from the
 * document that was actually served — `readLegalDocument(kind, locale).content_hash` — and the
 * `locale` sent beside it is the served one, which is not always the one that was asked for
 * (the AVV falls back to German by design).
 */
export async function acceptLegalDocument(offered: {
  kind: string;
  locale: string;
  contentHash: string;
}): Promise<Acceptance> {
  return unwrap(
    api.POST('/api/v1/legal-acceptances', {
      body: { kind: offered.kind, locale: offered.locale, content_hash: offered.contentHash },
    }),
  );
}
